import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addStaff } from "../../api/staff-calls-api";
import logo from "../../assets/logo.jpeg";
import {
    FiTrendingUp,

    FiUsers,
    FiMessageCircle,
    FiBell,
    FiFileText,
} from "react-icons/fi";
import { FaUserTie, FaUserCheck } from "react-icons/fa";

const Sidebar = () => {
    const links = [
        { label: "HR Dashboard", icon: <FiTrendingUp />, to: "/hr-dashboard" },
        { label: "Staff Profile", icon: <FiUsers />, to: "/staff-dashboard" },
        { label: "Recruitment", icon: <FaUserTie /> },
        { label: "Performance", icon: <FiFileText /> },
        { label: "Leave Requests", icon: <FaUserCheck /> },
        { label: "Messaging", icon: <FiMessageCircle /> },
        { label: "Documents", icon: <FiFileText /> },
        { label: "Payroll", icon: <FiFileText /> },
        { label: "Disciplinary Action", icon: <FiFileText /> },
    ];

    return (
        <aside className="w-64 bg-white shadow-md p-6 space-y-6">
            <div className="flex items-center justify-center">
                <img src={logo} alt="Logo" className="w-40" />
            </div>
            <nav className="space-y-4">
                {links.map((link, i) => (
                    <div key={i}>
                        {link.to ? (
                            <Link
                                to={link.to}
                                className="flex items-center gap-2 p-2 rounded-md hover:bg-orange-100 text-orange-600 font-bold"
                            >
                                {link.icon} {link.label}
                            </Link>
                        ) : (
                            <div className="flex items-center gap-2 p-2 rounded-md text-gray-700">
                                {link.icon} {link.label}
                            </div>
                        )}
                    </div>
                ))}
            </nav>
        </aside>
    );
};

const InputField = ({
    label,
    name,
    value,
    onChange,
    error,
    type = "text",
    options,
}) => (
    <div>
        <label className="block text-sm font-medium mb-1">{label}</label>
        {options ? (
            <select
                name={name}
                value={value || ""}
                onChange={onChange}
                className={`w-full border p-2 rounded ${error ? "border-red-500" : "border-gray-300"
                    }`}
            >
                <option value="">Select {label}</option>
                {options.map((option, idx) => (
                    <option key={idx} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        ) : (
            <input
                type={type}
                name={name}
                value={value || ""}
                onChange={onChange}
                className={`w-full border p-2 rounded ${error ? "border-red-500" : "border-gray-300"
                    }`}
            />
        )}
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
);

const AddStaffPage = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});
    const [showDropdown, setShowDropdown] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.firstName) newErrors.firstName = "First name is required";
        if (!form.lastName) newErrors.lastName = "Last name is required";
        if (!form.gender) newErrors.gender = "Gender is required";
        if (!form.email) newErrors.email = "Email is required";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        try {
            await addStaff(form);
            toast.success("Staff added successfully!");
            navigate("/staff-dashboard");
        } catch (err) {
            toast.error("Failed to add staff.");
        }
    };

    // Logout function clears token and redirects
    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.href = "/";
    };

    // Close dropdown on click outside
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest(".dropdown-container")) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const fields = {
        "Personal Information": [
            { name: "firstName", label: "First Name" },
            { name: "lastName", label: "Last Name" },
            { name: "gender", label: "Gender", options: ["Male", "Female"] },
            { name: "dateOfBirth", label: "Date of Birth", type: "date" },
            { name: "maritalStatus", label: "Marital Status", options: ["Single", "Married", "Other"] },
            { name: "nationality", label: "Nationality" },
            { name: "nationalIdOrPassport", label: "National ID or Passport" },
            { name: "villageOrBlock", label: "Village or Block" },
            { name: "payam", label: "Payam" },
            { name: "county", label: "County" },
            { name: "state", label: "State" },
            { name: "tel", label: "Telephone" },
            { name: "mobile", label: "Mobile" },
            { name: "email", label: "Email" },
        ],
        "Employment Details": [
            //{ name: "employeeId", label: "Employee ID" },
            { name: "jobTitle", label: "Job Title", options: ["Administration", "Finance Officer", "Human Resources", "Project Manager", "Operations Associate", "Head of Operations"] },
            { name: "department", label: "Department" },
            { name: "dateOfEmployment", label: "Date of Employment", type: "date" },
            { name: "employmentType", label: "Employment Type", options: ["Full-time", "Part-time", "Contract", "Internship"] },
            { name: "contractDuration", label: "Contract Duration" },
            { name: "salaryRange", label: "Salary Range" },
        ],
        "Bank Details": [
            { name: "bankName", label: "Bank Name" },
            { name: "bankBranch", label: "Bank Branch" },
            { name: "accountName", label: "Account Name" },
            { name: "accountNumber", label: "Account Number" },
            { name: "currency", label: "Currency" },
            { name: "swiftCode", label: "SWIFT Code" },
        ],
        "Education & Work History": [
            { name: "institution", label: "Institution" },
            { name: "award", label: "Award" },
            { name: "yearFrom", label: "Year From" },
            { name: "yearTo", label: "Year To" },
            { name: "keyExpertise", label: "Key Expertise" },
            { name: "recentEmployer", label: "Recent Employer" },
            { name: "role", label: "Role" },
            { name: "periodOfService", label: "Period of Service" },
            { name: "recentGrossAnnualPaymentRange", label: "Recent Gross Annual Payment Range" },
        ],
        "Next of Kin Information": [
            { name: "nokName", label: "Full Name" },
            { name: "nokSex", label: "Sex", options: ["Male", "Female", "Other"] },
            { name: "nokNationality", label: "Nationality" },
            { name: "nokRelationship", label: "Relationship" },
            { name: "nokVillageOrBlock", label: "Village or Block" },
            { name: "nokPayam", label: "Payam" },
            { name: "nokCounty", label: "County" },
            { name: "nokState", label: "State" },
            { name: "nokTel", label: "Telephone" },
            { name: "nokMobile", label: "Mobile" },
            { name: "nokEmail", label: "Email" },
        ],
    };

    return (
        <div className="flex h-screen bg-gray-50">
            <Sidebar />
            <main className="flex-1 p-6 overflow-y-auto">
                {/* Top right icons and avatar */}
                <div className="flex justify-end items-center gap-4 mb-6 dropdown-container relative">
                    <FiMessageCircle className="text-gray-600 text-xl" />
                    <FiBell className="text-gray-600 text-xl" />
                    <div>
                        <img
                            src="https://i.pravatar.cc/40"
                            alt="User"
                            className="rounded-full w-8 h-8 cursor-pointer"
                            onClick={() => setShowDropdown(!showDropdown)}
                        />
                        {showDropdown && (
                            <div className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg z-50">
                                <Link
                                    to="/profile"
                                    className="block px-4 py-2 text-gray-700 hover:bg-orange-100"
                                    onClick={() => setShowDropdown(false)}
                                >
                                    View Profile
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-orange-100"
                                >
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <h2 className="text-2xl font-bold text-orange-600 mb-6">Add New Staff</h2>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {Object.entries(fields).map(([section, inputs]) => (
                        <div key={section} className="bg-white p-6 rounded shadow">
                            <h3 className="text-lg font-semibold text-gray-700 mb-4">{section}</h3>
                            <div className="grid grid-cols-2 gap-6">
                                {inputs.map((field) => (
                                    <InputField
                                        key={field.name}
                                        {...field}
                                        value={form[field.name] || ""}
                                        onChange={handleChange}
                                        error={errors[field.name]}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                    <div className="text-right">
                        <button
                            type="submit"
                            className="bg-orange-600 text-white px-6 py-2 rounded hover:bg-orange-700"
                        >
                            Add New Staff
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
};

export default AddStaffPage;
